import { getApiUrl } from '@/config/api'
import type { CoachProfilePayload } from '@/types/coach'
import type {
  User,
  CreateUserPayload,
  CreateInstructorPayload,
  GenerateSignInLinkResponse,
  InviteUserRequest,
  InviteUserResponse,
  ProficiencySourcesResponse,
  ServiceAssignmentsResponse,
  UserEntitlement,
} from '@/types/user'
import type { UserFilterParams } from '@/types/userFilters'
export const userService = {
  // Get user by ID
  getUserUrl: (userId: string) => getApiUrl(`/users/${userId}`),

  // Get Clerk-specific user info (e.g., last login)
  getUserClerkInfoUrl: (userId: string) => getApiUrl(`/users/${userId}/clerk-info`),

  // Get email-deliverability verdict for a user (null if deliverable)
  getUserEmailDeliverabilityUrl: (userId: string) => getApiUrl(`/users/${userId}/email-deliverability`),

  // Get needs analysis for a user
  getNeedsAnalysisUrl: (userId: string) => getApiUrl(`/users/${userId}/needs-analysis`),

  // Get proficiency sources for a user
  getProficiencySourcesUrl: (userId: string) => getApiUrl(`/users/${userId}/proficiency-sources`),

  async getProficiencySources(userId: string, token: string): Promise<ProficiencySourcesResponse> {
    const response = await fetch(getApiUrl(`/users/${userId}/proficiency-sources`), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch proficiency sources: ${response.status}`)
    }
    return response.json()
  },

  // Get a learner's service assignments (coach)
  getServiceAssignmentsUrl: (userId: string) => getApiUrl(`/users/${userId}/service-assignments`),

  async getServiceAssignments(userId: string, token: string): Promise<ServiceAssignmentsResponse> {
    const response = await fetch(getApiUrl(`/users/${userId}/service-assignments`), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch service assignments: ${response.status}`)
    }
    return response.json()
  },

  // Get user invitation history
  getUserInvitationsUrl: (userId: string) => getApiUrl(`/users/${userId}/invitations`),

  // Get user status stats for an organization
  getUserStatsUrl: (organizationId: string) => getApiUrl(`/users/organization/${organizationId}/stats`),

  // Get users by organization with filters
  getFilteredOrganizationUsersUrl: (organizationId: string, filters: UserFilterParams) => {
    const url = new URL(getApiUrl(`/users/organization/${organizationId}`))

    // Add pagination parameters
    if (filters.limit !== undefined) {
      url.searchParams.set('limit', filters.limit.toString())
    }
    if (filters.offset !== undefined) {
      url.searchParams.set('offset', filters.offset.toString())
    }

    // Add role filters (use append for each value to send as array query params)
    if (filters.roles && filters.roles.length > 0) {
      filters.roles.forEach((role) => {
        url.searchParams.append('roles', role)
      })
    }

    // Add status filters (use append for each value to send as array query params)
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status) => {
        url.searchParams.append('statuses', status)
      })
    }

    // Add group filters (use append for each value to send as array query params)
    if (filters.group_ids && filters.group_ids.length > 0) {
      filters.group_ids.forEach((groupId) => {
        url.searchParams.append('group_ids', groupId)
      })
    }

    // Add no-group filter
    if (filters.include_no_group) {
      url.searchParams.set('include_no_group', 'true')
    }

    // Add name search filter
    if (filters.name) {
      url.searchParams.set('name', filters.name)
    }

    // Add license status filter
    if (filters.license_status) {
      url.searchParams.set('license_status', filters.license_status)
    }

    // Add sorting parameters
    if (filters.sort_by) {
      url.searchParams.set('sort_by', filters.sort_by)
    }
    if (filters.sort_order) {
      url.searchParams.set('sort_order', filters.sort_order)
    }

    return url.toString()
  },

  // Export filtered users as CSV
  getExportUsersUrl: (organizationId: string, filters: UserFilterParams) => {
    const url = new URL(getApiUrl(`/users/organization/${organizationId}/export`))

    if (filters.roles && filters.roles.length > 0) {
      filters.roles.forEach((role) => {
        url.searchParams.append('roles', role)
      })
    }
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status) => {
        url.searchParams.append('statuses', status)
      })
    }
    if (filters.group_ids && filters.group_ids.length > 0) {
      filters.group_ids.forEach((groupId) => {
        url.searchParams.append('group_ids', groupId)
      })
    }
    if (filters.include_no_group) {
      url.searchParams.set('include_no_group', 'true')
    }
    if (filters.name) {
      url.searchParams.set('name', filters.name)
    }
    if (filters.license_status) {
      url.searchParams.set('license_status', filters.license_status)
    }
    if (filters.sort_by) {
      url.searchParams.set('sort_by', filters.sort_by)
    }
    if (filters.sort_order) {
      url.searchParams.set('sort_order', filters.sort_order)
    }

    return url.toString()
  },

  // Get current user
  getCurrentUserUrl: () => getApiUrl('/users/me'),

  // Get current user's private accessibility preference
  getAccessibilityPreferencesUrl: () => getApiUrl('/users/me/accessibility-preferences'),

  // Search users (organization-scoped for client admins, global for super admins)
  getUserSearchUrl: (name: string, limit = 20, offset = 0, roles?: readonly string[], statuses?: readonly string[]) => {
    const url = new URL(getApiUrl('/users/search'))
    url.searchParams.set('name', name)
    url.searchParams.set('limit', limit.toString())
    url.searchParams.set('offset', offset.toString())
    if (roles && roles.length > 0) {
      for (const role of roles) {
        url.searchParams.append('roles', role)
      }
    }
    if (statuses && statuses.length > 0) {
      for (const s of statuses) {
        url.searchParams.append('statuses', s)
      }
    }
    return url.toString()
  },

  // Get instructors with optional capability filters
  getInstructorsUrl: (filters?: { canTeachGroupClasses?: boolean; canTeachPrivateClasses?: boolean }) => {
    const url = new URL(getApiUrl('/users/instructors'))
    if (filters?.canTeachGroupClasses !== undefined) {
      url.searchParams.set('can_teach_group_classes', String(filters.canTeachGroupClasses))
    }
    if (filters?.canTeachPrivateClasses !== undefined) {
      url.searchParams.set('can_teach_private_classes', String(filters.canTeachPrivateClasses))
    }
    return url.toString()
  },

  // Create user
  async createUser(userData: CreateUserPayload, token: string): Promise<User> {
    const response = await fetch(getApiUrl('/users'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to create user: ${response.status}`)
    }

    return response.json()
  },

  // Create instructor
  async createInstructor(instructorData: CreateInstructorPayload, token: string): Promise<User> {
    const response = await fetch(getApiUrl('/users/instructors'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(instructorData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to create instructor: ${response.status}`)
    }

    return response.json()
  },

  // Designate/update a coach's profile attributes for an existing EnGen user.
  async updateCoachProfile(userId: string, payload: CoachProfilePayload, token: string): Promise<void> {
    const response = await fetch(getApiUrl(`/admin/coaches/${userId}/profile`), {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to save coach: ${response.status}`)
    }
  },

  // Update user
  async updateUser(userId: string, userData: Partial<User>, token: string): Promise<User> {
    const response = await fetch(getApiUrl(`/users/${userId}`), {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to update user: ${response.status}`)
    }

    return response.json()
  },

  async deactivateUser(userId: string, token: string): Promise<void> {
    const response = await fetch(getApiUrl(`/users/${userId}`), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Failed to deactivate user: ${response.status}`)
    }
  },

  async changeEmail(
    userId: string,
    newEmail: string,
    token: string
  ): Promise<{ user_id: string; old_email: string; new_email: string; message: string }> {
    const response = await fetch(getApiUrl(`/users/${userId}/change-email`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ new_email: newEmail }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to change email: ${response.status}`)
    }

    return response.json()
  },

  async activateUser(userId: string, token: string): Promise<User> {
    const response = await fetch(getApiUrl(`/users/${userId}/activate`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Failed to activate user: ${response.status}`)
    }

    return response.json()
  },

  // Impersonate user - generates an actor token and returns the URL to sign in as the target user
  async impersonateUser(userId: string, token: string): Promise<{ token: string; url: string }> {
    const response = await fetch(getApiUrl(`/users/${userId}/impersonate`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to start impersonation: ${response.status}`)
    }

    return response.json()
  },

  // Invite user
  async inviteUser(userId: string, inviteData: InviteUserRequest, token: string): Promise<InviteUserResponse> {
    const response = await fetch(getApiUrl(`/users/${userId}/invite`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inviteData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to invite user: ${response.status}`)
    }

    return response.json()
  },

  // Generate a single-use sign-in token. Default purpose returns the URL
  // for out-of-band sharing; password_reset emails the user a reset link
  // and returns no URL.
  async generateSignInLink(
    userId: string,
    token: string,
    purpose: 'admin_assist' | 'password_reset' = 'admin_assist'
  ): Promise<GenerateSignInLinkResponse> {
    const response = await fetch(getApiUrl(`/users/${userId}/sign-in-link`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ purpose }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to generate sign-in link: ${response.status}`)
    }

    return response.json()
  },

  // Convenience wrapper: trigger a password-reset email to the user.
  async sendPasswordResetEmail(userId: string, token: string): Promise<GenerateSignInLinkResponse> {
    return this.generateSignInLink(userId, token, 'password_reset')
  },

  // Get user entitlements URL
  getEntitlementsUrl: (userId: string) => getApiUrl(`/users/${userId}/entitlements`),

  // Get user entitlement history URL
  getEntitlementHistoryUrl: (userId: string, includeRevoked = true) => {
    const url = new URL(getApiUrl(`/users/${userId}/entitlements/history`))
    url.searchParams.set('include_revoked', String(includeRevoked))
    return url.toString()
  },

  // Grant entitlement to user
  async grantEntitlement(userId: string, entitlementKey: string, token: string): Promise<UserEntitlement> {
    const response = await fetch(getApiUrl(`/users/${userId}/entitlements/${entitlementKey}`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to grant entitlement: ${response.status}`)
    }

    return response.json()
  },

  async changeOrganization(
    userId: string,
    newOrganizationId: string,
    newGroupId: string | null,
    token: string
  ): Promise<{ user_id: string; old_organization_id: string; new_organization_id: string; message: string }> {
    const response = await fetch(getApiUrl(`/users/${userId}/change-organization`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ new_organization_id: newOrganizationId, new_group_id: newGroupId }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to change organization: ${response.status}`)
    }

    return response.json()
  },

  // Revoke entitlement from user
  async revokeEntitlement(
    userId: string,
    entitlementKey: string,
    reason: string | null,
    token: string
  ): Promise<UserEntitlement> {
    const url = new URL(getApiUrl(`/users/${userId}/entitlements/${entitlementKey}`))
    if (reason) {
      url.searchParams.set('reason', reason)
    }

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Failed to revoke entitlement: ${response.status}`)
    }

    return response.json()
  },
}
